import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Descriptions, Drawer, Form, Input, Modal, Select, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import {
  archivePolicy,
  createPolicy,
  getPolicy,
  listPolicies,
  publishPolicy,
  type AbacPolicy,
} from '@/api/teamAccess';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/auth/OlAuthContext';
import { formatAbsolute } from '@/lib/time';
import {
  conditionToJson,
  emptyCondition,
  jsonToCondition,
  validateCondition,
  type AbacConditionModel,
} from './abacCondition';
import { AbacConditionBuilder } from './AbacConditionBuilder';

const { Paragraph, Text } = Typography;

/**
 * The §6.3-eligible keys — AclKeyMap-mapped, mutating capabilities only; the server refuses
 * anything else. runs.start joined the map with OPSFL-FLOWOPS-1 C1 ((flow, work) — a spoke-fenced
 * start), so a DENY policy over it is now server-eligible too.
 */
const ELIGIBLE_KEYS = [
  'workitems.assign', 'workitems.disposition', 'flows.author', 'flows.publish', 'runs.start',
  'rfi.create', 'rfi.send', 'dataroom.invites.create',
];

interface CreateForm {
  policyCode: string;
  permissionKey: string;
  note?: string;
}

/**
 * B1 · UC2 §6 — ABAC policies (Team & Access). DENY-only conditions over the closed attribute
 * registry. The condition is authored BUILDER-FIRST (the Screen Studio pattern): a registry-aware
 * builder that offers ONLY legal paths/ops, with "Advanced (JSON)" as a synced power-user view
 * over the SAME condition; a JSON shape the builder cannot represent keeps the JSON tab
 * authoritative and says so. Publish bites on the next request; a published condition is never
 * edited — new version, archive the old. The console hides, the server re-checks (§12).
 */
export function PoliciesPanel() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const canWrite = user?.roles?.includes('hub_admin') ?? false;
  const [policies, setPolicies] = useState<AbacPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CreateForm>();

  // The dual representation (EditorDrawer pattern): the typed MODEL is canonical while the
  // builder can express the JSON; a divergent JSON buffer keeps its own text so half-typed JSON
  // never destroys builder state, and a VALID parse flows back into the model immediately.
  const [model, setModel] = useState<AbacConditionModel>(emptyCondition);
  const [jsonText, setJsonText] = useState(() => JSON.stringify(conditionToJson(emptyCondition()), null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  /** Non-null when the JSON holds a shape the builder cannot represent — JSON is then the source. */
  const [builderStandsAside, setBuilderStandsAside] = useState(false);
  const [tab, setTab] = useState('builder');

  const [detail, setDetail] = useState<AbacPolicy | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      setPolicies(await listPolicies());
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    const fresh = emptyCondition();
    setModel(fresh);
    setJsonText(JSON.stringify(conditionToJson(fresh), null, 2));
    setJsonError(null);
    setBuilderStandsAside(false);
    setTab('builder');
    setModalOpen(true);
  };

  const onModelChange = (next: AbacConditionModel) => {
    setModel(next);
    setJsonText(JSON.stringify(conditionToJson(next), null, 2));
    setJsonError(null);
    setBuilderStandsAside(false);
  };

  const onJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed: unknown = JSON.parse(text);
      setJsonError(null);
      const parsedModel = jsonToCondition(parsed);
      if (parsedModel) {
        setModel(parsedModel);
        setBuilderStandsAside(false);
      } else {
        setBuilderStandsAside(true);
      }
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : String(e));
    }
  };

  /** The condition that will be submitted — the JSON buffer when it is the source of truth. */
  const effectiveCondition = (): unknown | null => {
    if (jsonError) return null;
    if (builderStandsAside) {
      try {
        return JSON.parse(jsonText);
      } catch {
        return null;
      }
    }
    return conditionToJson(model);
  };

  const preflight = (() => {
    const condition = effectiveCondition();
    return condition === null ? [] : validateCondition(condition);
  })();

  const act = async (fn: () => Promise<unknown>, success: string) => {
    try {
      await fn();
      message.success(success);
      void load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    }
  };

  const openDetail = async (p: AbacPolicy) => {
    setDetail(p); // render the row facts immediately…
    setDetailLoading(true);
    try {
      setDetail(await getPolicy(p.policyId)); // …then the server's authoritative view
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns: ColumnsType<AbacPolicy> = [
    {
      title: 'Policy',
      key: 'code',
      render: (_: unknown, p: AbacPolicy) => (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => void openDetail(p)}>
          <Text code>{p.policyCode}</Text> <Text type="secondary">v{p.version}</Text>
        </Button>
      ),
    },
    { title: 'Denies key', dataIndex: 'permissionKey', render: (v: string) => <Tag>{v}</Tag> },
    {
      title: 'Condition',
      dataIndex: 'condition',
      render: (c: unknown) => (
        <Text code style={{ fontSize: 11, whiteSpace: 'nowrap', maxWidth: 340, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {JSON.stringify(c)}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => (
        <Tag color={s === 'PUBLISHED' ? 'green' : s === 'DRAFT' ? 'blue' : 'default'}>{s}</Tag>
      ),
    },
    { title: 'Created', dataIndex: 'createdAt', width: 170, render: (v) => formatAbsolute(v) },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'actions',
            width: 160,
            render: (_: unknown, p: AbacPolicy) => (
              <span style={{ display: 'inline-flex', gap: 8 }}>
                {p.status === 'DRAFT' && (
                  <Button size="small" type="primary" onClick={() =>
                    void act(() => publishPolicy(p.policyId),
                      `${p.policyCode} published — denies on the next matching request`)}>
                    Publish
                  </Button>
                )}
                {p.status !== 'ARCHIVED' && (
                  <Button size="small" danger onClick={() =>
                    void act(() => archivePolicy(p.policyId), `${p.policyCode} archived`)}>
                    Archive
                  </Button>
                )}
              </span>
            ),
          },
        ]
      : []),
  ];

  if (unavailable) {
    return <EmptyState title="ABAC policies are not available" description="The IAM UC2 surface is not enabled in this deployment." />;
  }

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12, maxWidth: 780, fontSize: 13 }}>
        Attribute policies DENY, never allow — refinement cannot widen, by construction. Seven mutating
        keys are eligible (a view-denying policy would desynchronise queues from the point guard, so it
        is refused). Conditions use the closed attribute registry; anything the evaluator cannot resolve
        DENIES. Publish is next-request; edit = new version.
      </Paragraph>
      {canWrite && (
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ marginBottom: 12 }}>
          New policy
        </Button>
      )}
      <Table rowKey="policyId" size="small" columns={columns} dataSource={policies} loading={loading}
             pagination={{ pageSize: 20, hideOnSinglePage: true }} />

      <Modal title="New ABAC policy (DRAFT)" open={modalOpen} onCancel={() => setModalOpen(false)}
             onOk={() => form.submit()} confirmLoading={submitting} okText="Create draft" width={720}
             okButtonProps={{ disabled: jsonError !== null }}>
        <Form form={form} layout="vertical" onFinish={async (v) => {
          const condition = effectiveCondition();
          if (condition === null) {
            message.error('The condition JSON has a syntax error — fix it on the Advanced tab.');
            return;
          }
          setSubmitting(true);
          try {
            await createPolicy({
              policyCode: v.policyCode.trim(), permissionKey: v.permissionKey,
              condition, note: v.note?.trim() || null,
            });
            message.success(`${v.policyCode.trim()} drafted`);
            setModalOpen(false);
            form.resetFields();
            void load();
          } catch (e) {
            message.error(normalizeApiError(e).message);
          } finally {
            setSubmitting(false);
          }
        }}>
          <Form.Item name="policyCode" label="Policy code" rules={[{ required: true }]}>
            <Input placeholder="no-offshore-dispositions" />
          </Form.Item>
          <Form.Item name="permissionKey" label="Denies key" rules={[{ required: true }]}>
            <Select options={ELIGIBLE_KEYS.map((k) => ({ value: k }))} showSearch />
          </Form.Item>

          <Form.Item label="Condition" required style={{ marginBottom: 8 }}>
            <Tabs
              activeKey={tab}
              onChange={setTab}
              size="small"
              items={[
                {
                  key: 'builder',
                  label: 'Builder',
                  children: builderStandsAside ? (
                    <Alert
                      type="info"
                      showIcon
                      message="This condition is not a legal builder shape"
                      description="The JSON tab holds something outside the closed vocabulary or the legal grammar envelope (registry paths, five ops, depth ≤ 3). The builder stands aside rather than rewrite it — edit the JSON, or fix it until it parses as a legal condition."
                    />
                  ) : (
                    <AbacConditionBuilder model={model} onChange={onModelChange} />
                  ),
                },
                {
                  key: 'json',
                  label: 'Advanced (JSON)',
                  children: (
                    <div>
                      <Input.TextArea
                        rows={6}
                        className="ol-mono"
                        style={{ fontSize: 12 }}
                        value={jsonText}
                        onChange={(e) => onJsonChange(e.target.value)}
                        aria-label="Condition JSON"
                      />
                      {jsonError && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          {jsonError}
                        </Text>
                      )}
                    </div>
                  ),
                },
              ]}
            />
            {preflight.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message="The publish gate will refuse this condition"
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {preflight.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {!builderStandsAside && !jsonError && (
              <Paragraph type="secondary" style={{ fontSize: 12, margin: '8px 0 0' }}>
                Will submit: <Text code style={{ fontSize: 11 }}>{JSON.stringify(conditionToJson(model))}</Text>
              </Paragraph>
            )}
          </Form.Item>

          <Form.Item name="note" label="Note">
            <Input placeholder="why this policy exists" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detail ? `Policy — ${detail.policyCode} v${detail.version}` : 'Policy'}
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={560}
      >
        {detail && (
          <div>
            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                { key: 'key', label: 'Denies key', children: <Tag>{detail.permissionKey}</Tag> },
                {
                  key: 'status', label: 'Status',
                  children: (
                    <Tag color={detail.status === 'PUBLISHED' ? 'green' : detail.status === 'DRAFT' ? 'blue' : 'default'}>
                      {detail.status}
                    </Tag>
                  ),
                },
                { key: 'note', label: 'Note', children: detail.note ?? '—' },
                { key: 'created', label: 'Created', children: `${formatAbsolute(detail.createdAt)} by ${detail.createdBy}` },
                {
                  key: 'published', label: 'Published',
                  children: detail.publishedAt ? `${formatAbsolute(detail.publishedAt)} by ${detail.publishedBy ?? '—'}` : '—',
                },
                { key: 'archived', label: 'Archived', children: detail.archivedAt ? formatAbsolute(detail.archivedAt) : '—' },
              ]}
            />
            <Paragraph style={{ marginTop: 16, marginBottom: 4 }}>
              <Text strong>Condition</Text>{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {detailLoading ? '(refreshing from the server…)' : '(the server-held condition, verbatim)'}
              </Text>
            </Paragraph>
            <pre className="ol-mono" style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
              {JSON.stringify(detail.condition, null, 2)}
            </pre>
          </div>
        )}
      </Drawer>
    </div>
  );
}
